/**
 * TraceHeader Component
 *
 * Sticky header showing execution status, progress bar, and quick stats.
 * Always visible at the top of the trace view.
 */

import { Component, Show } from 'solid-js';
import type { ExecutionPhase, ExecutionStats, ExecutionProgress } from '../../../stores/executionStore.ts';

export interface TraceHeaderProps {
  agentName?: string;
  status: string;
  phase: ExecutionPhase;
  progress: ExecutionProgress;
  stats: ExecutionStats;
  isLive: boolean;
  formatDuration: (ms?: number) => string;
}

const TraceHeader: Component<TraceHeaderProps> = (props) => {
  const getStatusColor = () => {
    switch (props.status) {
      case 'running':
        return 'text-blue-400';
      case 'completed':
        return 'text-green-400';
      case 'failed':
        return 'text-red-400';
      case 'cancelled':
        return 'text-gray-400';
      default:
        return 'text-yellow-400';
    }
  };

  const getProgressBarColor = () => {
    switch (props.phase) {
      case 'completed':
        return 'bg-green-500';
      case 'failed':
        return 'bg-red-500';
      case 'cancelled':
        return 'bg-gray-500';
      default:
        return 'bg-blue-500';
    }
  };

  return (
    <div class="bg-gray-800/80 backdrop-blur-sm rounded-lg border border-gray-700 p-4 sticky top-0 z-10">
      {/* Top row: Agent name, status, duration */}
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-3">
          <Show when={props.agentName}>
            <span class="text-gray-200 font-medium">{props.agentName}</span>
          </Show>
          <div class="flex items-center gap-2">
            <Show when={props.status === 'running'}>
              <span class="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
            </Show>
            <Show when={props.status === 'completed'}>
              <span class="w-2 h-2 rounded-full bg-green-400" />
            </Show>
            <Show when={props.status === 'failed'}>
              <span class="w-2 h-2 rounded-full bg-red-400" />
            </Show>
            <Show when={props.status === 'cancelled'}>
              <span class="w-2 h-2 rounded-full bg-gray-400" />
            </Show>
            <Show when={props.status === 'pending'}>
              <span class="w-2 h-2 rounded-full bg-yellow-400" />
            </Show>
            <span class={`text-sm font-medium capitalize ${getStatusColor()}`}>
              {props.status}
            </span>
          </div>
        </div>
        <div class="text-sm text-gray-400">
          {props.formatDuration(props.stats.totalDurationMs)}
        </div>
      </div>

      {/* Progress bar */}
      <div class="mb-4">
        <div class="h-1.5 bg-gray-700 rounded-full overflow-hidden">
          <div
            class={`h-full rounded-full transition-all duration-300 ${getProgressBarColor()}`}
            style={{ width: `${props.progress.percentage}%` }}
          />
        </div>
        <div class="flex justify-between items-center mt-1">
          <span class="text-xs text-gray-500">
            {props.progress.percentage}% complete
          </span>
          <Show when={props.progress.currentStep}>
            <span class="text-xs text-gray-400 truncate max-w-md">
              {props.progress.currentStep}
            </span>
          </Show>
        </div>
      </div>

      {/* Quick stats */}
      <div class="flex items-center gap-6 text-sm">
        <div class="flex items-center gap-2">
          <svg class="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span class="text-gray-300">{props.stats.toolCallCount}</span>
          <span class="text-gray-500">tools</span>
        </div>

        <div class="flex items-center gap-2">
          <svg class="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <span class="text-gray-300">{props.stats.llmCallCount}</span>
          <span class="text-gray-500">LLM calls</span>
        </div>

        <div class="flex items-center gap-2">
          <svg class="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          <span class="text-gray-300">{props.stats.totalTokens.toLocaleString()}</span>
          <span class="text-gray-500">tokens</span>
        </div>

        <Show when={props.stats.errorCount > 0}>
          <div class="flex items-center gap-2">
            <svg class="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span class="text-red-400">{props.stats.errorCount}</span>
            <span class="text-gray-500">errors</span>
          </div>
        </Show>

        <Show when={props.isLive}>
          <div class="flex items-center gap-1 ml-auto">
            <span class="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span class="text-xs text-green-400">Live</span>
          </div>
        </Show>
      </div>
    </div>
  );
};

export default TraceHeader;
